import { staleTimes, useMutation, useQuery } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { usePageContext } from '../../usePageContext.ts';
import type { DashboardLayout, DashboardView } from './widget-types.ts';

const VIEWS_RESOURCE = 'dashboard-views';

export const useDashboardViews = (api: TypedClient) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: [VIEWS_RESOURCE, tenantId],
    queryFn: () => api.get('/admin/me/dashboard-views', {}),
    staleTime: staleTimes.settings,
  });
};

export type ViewSave =
  | { readonly kind: 'create'; readonly name: string; readonly layout: DashboardLayout }
  | {
      readonly kind: 'update';
      readonly view: DashboardView;
      readonly name: string;
      readonly layout: DashboardLayout;
    }
  | { readonly kind: 'delete'; readonly view: DashboardView }
  | { readonly kind: 'default'; readonly view: DashboardView };

const sendOf =
  (api: TypedClient) =>
  async (save: ViewSave): Promise<DashboardView | null> => {
    if (save.kind === 'create')
      return api.post('/admin/me/dashboard-views', { body: { name: save.name, layout: save.layout } });
    if (save.kind === 'update')
      return api.put('/admin/me/dashboard-views/{id}', {
        path: { id: save.view.id },
        body: { name: save.name, layout: save.layout, version: save.view.version },
      });
    if (save.kind === 'default')
      return api.post('/admin/me/dashboard-views/{id}/default', {
        path: { id: save.view.id },
        body: { version: save.view.version },
      });
    await api.delete('/admin/me/dashboard-views/{id}', { path: { id: save.view.id } });
    return null;
  };

const SAVED_MESSAGES: Readonly<Record<ViewSave['kind'], string>> = {
  create: 'dashboard.views.created',
  update: 'dashboard.views.updated',
  delete: 'dashboard.views.deleted',
  default: 'dashboard.views.madeDefault',
};

export const useDashboardViewChanges = (api: TypedClient) => {
  const { t, notify, warn, describeError, invalidate } = usePageContext();
  return useMutation({
    mutationFn: sendOf(api),
    onSuccess: async (_view, save) => {
      notify(t(SAVED_MESSAGES[save.kind]));
      await invalidate(VIEWS_RESOURCE);
    },
    onError: async (error) => {
      warn(describeError(error) ?? t('dashboard.views.failed'));
      await invalidate(VIEWS_RESOURCE);
    },
  });
};

export const defaultViewOf = (views: readonly DashboardView[]): DashboardView | undefined =>
  views.find((view) => view.isDefault);
